/*
 * PASS-943 Modal components taken from @rally/connect-components (a.k.a telemedicine-components)
 * adjusted for RDS-based styling
 */
import React, { Component, RefObject, createRef, HTMLAttributes } from 'react';
import { RDS_BREAKPOINTS_DESKTOP } from '@rally/ui-themes/dist/web/rally/tokens.es6.js';
import styled from 'styled-components';

import { MEDIA_QUERY } from '@pass-frontend/utility/style-helpers';
import {
  Props as BaseModalProps,
  BaseModal,
  ModalContainer,
} from './base-modal';
import { ModalBody as BaseModalBody } from './modal-body';
import ModalCloseButton from './modal-close-button';
import {
  ModalFooter as BaseModalFooter,
  Props as ModalFooterProps,
} from './modal-footer';
import {
  ModalHeader as BaseModalHeader,
  Props as ModalHeaderProps,
} from './modal-header';

export interface ModalProps extends BaseModalProps {
  heading?: ModalHeaderProps['heading'];
  ariaLabel?: ModalHeaderProps['ariaLabel'];
  shouldHideHeading?: ModalHeaderProps['shouldHideHeading'];
  subHeading?: ModalHeaderProps['subHeading'];
  cancelButtonText?: string;
  actionButtonText?: string;
  footing?: ModalFooterProps['children'];
  closeButton?: boolean;
  closeButtonProps?: HTMLAttributes<HTMLButtonElement>;
  areFooterButtonsRowAligned?: boolean;
  imageSource?: string;
}

const StyledBaseModal = styled(BaseModal)`
  align-items: center;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  height: 100%;
  max-height: 100%;
  max-width: 100%;
  overflow: auto;
  position: relative;
  width: 100%;

  ${MEDIA_QUERY.desktop} {
    height: auto;
    max-width: 832px;
    width: auto;
  }

  ${MEDIA_QUERY.mobileAndTablet} {
    height: calc(100% - 48px);
    margin-top: 48px;
  }

  ${ModalContainer} {
    align-self: stretch;
    display: flex;
    flex-direction: column;
    max-width: ${RDS_BREAKPOINTS_DESKTOP};
    overflow: auto;
    flex-grow: 1;
  }
`;

const ModalHeader = styled(BaseModalHeader)<ModalHeaderProps>`
  flex-shrink: 0;
`;

const ModalFooter = styled(BaseModalFooter)<ModalFooterProps>`
  flex-shrink: 0;
`;

const ModalLogo = styled.img`
  margin-top: 32px;
  max-width: 96px;
  max-height: 96px;
  margin-left: auto;
  margin-right: auto;
`;
//interesting - even though deleted previous click handlers seems that basemodalbody overtakes click references
//this component should be investigated, as seems that propagation of events is overtaken from this Modal to BaseModalBody
//but modal-footer renders the buttons that don't do anything
export const StyledModalBody = styled(BaseModalBody)`
  flex-grow: 1;
  overflow: auto;
`;

export class Modal extends Component<ModalProps> {
  static defaultProps = {
    closeButton: true,
    shouldCloseOnEsc: true,
    shouldCloseOnOverlayClick: true,
  };
  private baseModalRef: RefObject<BaseModal>;

  constructor(props: ModalProps) {
    super(props);
    this.baseModalRef = createRef();
  }

  render() {
    const {
      children,
      heading,
      ariaLabel,
      shouldHideHeading,
      subHeading,
      cancelButtonText,
      actionButtonText,
      footing,
      closeButton,
      closeButtonProps,
      areFooterButtonsRowAligned,
      // TODO we can auto-generate UUID for ariaHeadingId, as long as it's the same across runtime instances
      ariaHeadingId,
      imageSource,
      ...modalProps
    } = this.props;
    const headingId = ariaHeadingId || `headingId-${StyledModalBody}`;

    const ariaLabelTxt: string =
      typeof ariaLabel === 'string'
        ? ariaLabel
        : typeof heading === 'string'
          ? heading
          : '';

    return (
      <StyledBaseModal
        {...modalProps}
        ref={this.baseModalRef}
        ariaHeadingId={headingId}
      >
        {closeButton && (
          <ModalCloseButton
            onClick={() => this.baseModalRef.current?.onRequestedClose()}
            aria-label={`Close ${ariaLabelTxt}`}
            {...closeButtonProps}
          />
        )}
        {imageSource && <ModalLogo src={imageSource} />}
        <ModalHeader
          heading={heading}
          shouldHideHeading={shouldHideHeading}
          subHeading={subHeading}
          ariaHeadingId={headingId}
          className={(!heading && 'is-sr-only') || undefined}
        />
        <StyledModalBody>{children}</StyledModalBody>
        <ModalFooter
          className="is-flex is-justify-content-center"
          primaryButtonText={actionButtonText}
          secondaryButtonText={cancelButtonText}
          primaryButtonClassName="action-btn"
          secondaryButtonClassName="next-action-btn cancel-btn close-btn"
          areFooterButtonsRowAligned={areFooterButtonsRowAligned}
        >
          {footing}
        </ModalFooter>
      </StyledBaseModal>
    );
  }
}

export default Modal;
